import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const ENTER_MS = 650;
const EXIT_MS = 500;

const BOARD_CLASSES = { cork: 'lw-board-cork', dark: 'lw-board-dark', light: 'lw-board-light' };
const CARD_SIZES = {
    small:  { photo: 120, pad: 28, font: 11 },
    medium: { photo: 160, pad: 36, font: 12 },
    large:  { photo: 210, pad: 46, font: 14 },
};
const PIN_COLORS = [
    { c: '#f87171', r: '#dc2626' }, { c: '#60a5fa', r: '#2563eb' },
    { c: '#fbbf24', r: '#d97706' }, { c: '#4ade80', r: '#16a34a' },
    { c: '#c084fc', r: '#9333ea' }, { c: '#fb923c', r: '#ea580c' },
];
const ROTATION_SCALE = { none: 0, subtle: 1, wild: 1.8 };

// Grid layout varies by card size so smaller cards fill the board more densely.
// Row Y positions start at 12% — well below the top bar.
const GRID_CONFIGS = {
    small:  { cols: 8, colX: [1, 13, 26, 39, 51, 63, 76, 88], rowY: [12, 40, 68], maxVisible: 24 },
    medium: { cols: 7, colX: [1, 15, 29, 43, 57, 71, 85],     rowY: [12, 42, 71], maxVisible: 21 },
    large:  { cols: 6, colX: [1, 18, 35, 52, 69, 84],          rowY: [12, 44, 76], maxVisible: 18 },
};

// Positions are stable within a session (keyed by item id).
const metaMap = new Map();

/**
 * Assign a grid position to an item. Prefers slots not currently occupied so the
 * board fills evenly. The caller must pass the Set of currently occupied slot indices
 * and add the newly assigned slot to it after calling (for batched arrivals).
 * permanentlyBlocked contains slots that should never be used (e.g. QR code safe zone).
 */
function assignMeta(id, config, occupiedSlots, permanentlyBlocked = new Set()) {
    if (metaMap.has(id)) return;

    const totalSlots = config.cols * config.rowY.length;
    const allSlots = Array.from({ length: totalSlots }, (_, i) => i).filter(s => !permanentlyBlocked.has(s));
    const emptySlots = allSlots.filter(s => !occupiedSlots.has(s));
    const candidates = emptySlots.length > 0 ? emptySlots : allSlots;
    const slot = candidates[Math.floor(Math.random() * candidates.length)];
    const col = slot % config.cols;
    const row = Math.floor(slot / config.cols);

    metaMap.set(id, {
        slot,
        x: config.colX[col] + (Math.random() * 8) - 4,  // ±4% horizontal jitter
        y: config.rowY[row] + (Math.random() * 6) - 3,  // ±3% vertical jitter
        rotation: (Math.random() * 24) - 12,              // −12° to +12°
        pinIdx: Math.floor(Math.random() * PIN_COLORS.length),
    });
}

function getMeta(id) {
    return metaMap.get(id) ?? { slot: 0, x: 50, y: 50, rotation: 0, pinIdx: 0 };
}

function PolaroidCard({ boardItem, size, settings, rotationScale }) {
    const [phase, setPhase] = useState(boardItem.isNew ? 'entering' : 'idle');

    useEffect(() => {
        if (boardItem.exiting) setPhase('exiting');
    }, [boardItem.exiting]);

    useEffect(() => {
        if (phase !== 'entering') return;
        const t = setTimeout(() => setPhase('idle'), ENTER_MS + 50);
        return () => clearTimeout(t);
    }, [phase]);

    const meta = getMeta(boardItem.id);
    const pin = PIN_COLORS[meta.pinIdx];
    const rotation = meta.rotation * rotationScale;
    const cardWidth = size.photo + 16;
    const animClass = phase === 'entering' ? 'lw-polaroid-new' : phase === 'exiting' ? 'lw-polaroid-exit' : '';

    return (
        <div
            className={animClass}
            style={{
                position: 'absolute',
                left: `${meta.x}%`,
                top: `${meta.y}%`,
                '--lw-rotate': `${rotation}deg`,
                transform: `rotate(${rotation}deg)`,
                // Entering cards pop to z:10 to appear above everything during the drop
                // animation. Idle/exiting cards rely on DOM order: boardItems is rendered
                // oldest-first so newer cards are later in the DOM and naturally on top.
                zIndex: phase === 'entering' ? 10 : 1,
                willChange: 'transform, opacity',
            }}
        >
            {/* Pin */}
            <div style={{
                position: 'absolute', left: '50%', top: '-10px',
                transform: 'translateX(-50%)',
                width: '18px', height: '18px', borderRadius: '50%',
                background: `radial-gradient(circle at 38% 35%, ${pin.c}, ${pin.r})`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.55), inset 0 1px 2px rgba(255,255,255,0.35)',
                zIndex: 10,
            }} />

            {/* Polaroid frame — bottom padding and caption grow with content */}
            <div style={{
                backgroundColor: '#fff',
                padding: '8px 8px 10px',
                width: `${cardWidth}px`,
                boxShadow: '0 8px 28px rgba(0,0,0,0.45), 0 1px 4px rgba(0,0,0,0.18)',
            }}>
                <div style={{
                    width: `${size.photo}px`, height: `${size.photo}px`,
                    overflow: 'hidden', backgroundColor: '#d1d5db', position: 'relative',
                }}>
                    {boardItem.file_type === 'video' ? (
                        <video
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            src={boardItem.url} autoPlay muted loop playsInline
                        />
                    ) : (
                        <img
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            src={boardItem.preview_url || boardItem.url}
                            alt="" loading="lazy"
                        />
                    )}
                </div>

                {(settings.showCaptions && boardItem.caption) || (settings.showNames && boardItem.uploader_name) ? (
                    <div style={{
                        paddingTop: '6px', display: 'flex', flexDirection: 'column',
                        alignItems: 'center', gap: '2px',
                    }}>
                        {settings.showCaptions && boardItem.caption && (
                            <span style={{
                                fontSize: `${size.font}px`, color: '#374151', textAlign: 'center',
                                fontStyle: 'italic', fontFamily: 'Georgia, serif', lineHeight: 1.25,
                                maxWidth: '100%', overflow: 'hidden',
                                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                            }}>{boardItem.caption}</span>
                        )}
                        {settings.showNames && boardItem.uploader_name && (
                            <span style={{
                                fontSize: `${size.font - 1}px`, color: '#9ca3af',
                                fontFamily: 'Georgia, serif', maxWidth: '100%',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>{boardItem.uploader_name}</span>
                        )}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

// Bottom-left slot (last row, col 0) is reserved as QR code safe zone.
function getQrBlockedSlot(config) {
    return (config.rowY.length - 1) * config.cols;
}

export default function PolaroidBoard({ items, settings, showQr = false }) {
    const { t } = useTranslation();
    const sizeKey = settings.cardSize ?? 'medium';
    const gridConfig = GRID_CONFIGS[sizeKey];
    const maxVisible = gridConfig.maxVisible;
    const permanentlyBlocked = showQr ? new Set([getQrBlockedSlot(gridConfig)]) : new Set();

    const [boardItems, setBoardItems] = useState(() => {
        // Slice from the end so we seed with the most-recent items (items is oldest-first)
        const initial = items.slice(-maxVisible);
        const occupiedSlots = new Set();
        initial.forEach(item => {
            assignMeta(item.id, gridConfig, occupiedSlots, permanentlyBlocked);
            occupiedSlots.add(metaMap.get(item.id).slot);
        });
        return initial.map(item => ({ ...item, exiting: false }));
    });

    // Track ALL initial items so the mount effect doesn't treat items beyond maxVisible as "new"
    const knownIds = useRef(new Set(items.map(i => i.id)));
    const pruneTimerRef = useRef(null);

    // Clear cached positions on unmount so a re-mount (navigation to different event or
    // card-size change) starts fresh rather than reusing stale grid coordinates.
    useEffect(() => () => metaMap.clear(), []);

    useEffect(() => {
        const newItems = items.filter(i => !knownIds.current.has(i.id));
        newItems.forEach(i => knownIds.current.add(i.id));

        setBoardItems(prev => {
            if (!newItems.length) {
                // No new arrivals — only prune if maxVisible decreased (e.g. card size changed)
                const visible = prev.filter(i => !i.exiting);
                if (visible.length <= maxVisible) return prev;
                const toExit = visible.length - maxVisible;
                let exited = 0;
                return prev.map((item, idx) => {
                    if (!item.exiting && exited < toExit && idx >= prev.length - toExit) {
                        exited++;
                        return { ...item, exiting: true };
                    }
                    return item;
                });
            }

            // Determine which existing items will exit BEFORE assigning new positions,
            // so their slots are treated as free and new photos land there immediately.
            const currentVisible = prev.filter(i => !i.exiting);
            const willExitCount = Math.max(0, currentVisible.length + newItems.length - maxVisible);
            const exitingIds = new Set();
            if (willExitCount > 0) {
                let count = 0;
                for (let i = prev.length - 1; i >= 0 && count < willExitCount; i--) {
                    if (!prev[i].exiting) {
                        exitingIds.add(prev[i].id);
                        count++;
                    }
                }
            }

            // Occupied slots = current non-exiting, minus the ones just freed
            const occupiedSlots = new Set(
                prev.filter(i => !i.exiting && !exitingIds.has(i.id) && metaMap.has(i.id))
                    .map(i => metaMap.get(i.id).slot)
            );

            const placed = newItems.map(i => {
                assignMeta(i.id, gridConfig, occupiedSlots, permanentlyBlocked);
                occupiedSlots.add(metaMap.get(i.id).slot);
                return { ...i, exiting: false };
            });

            const next = [...placed, ...prev];
            return next.map(item => ({
                ...item,
                exiting: item.exiting || exitingIds.has(item.id),
            }));
        });

        clearTimeout(pruneTimerRef.current);
        pruneTimerRef.current = setTimeout(() => {
            setBoardItems(prev => prev.filter(i => !i.exiting));
        }, EXIT_MS + 100);

        return () => clearTimeout(pruneTimerRef.current);
    }, [items, maxVisible, gridConfig]);

    const boardClass = BOARD_CLASSES[settings.theme ?? 'cork'];
    const size = CARD_SIZES[sizeKey];
    const rotScale = ROTATION_SCALE[settings.rotation ?? 'subtle'];

    if (!boardItems.length) {
        return (
            <div className={`${boardClass} flex items-center justify-center w-full h-full`}>
                <p className="text-amber-950/40 text-2xl font-medium">{t('Waiting for the first photo…')}</p>
            </div>
        );
    }

    // Render oldest cards first so newer cards sit later in the DOM and are naturally
    // on top when two cards share the same grid slot (same CSS stacking context, no
    // explicit z-index needed for the idle state).
    const renderOrder = [...boardItems].reverse();

    return (
        <div className={`${boardClass} relative w-full h-full overflow-hidden`}>
            {renderOrder.map(item => (
                <PolaroidCard
                    key={item.id}
                    boardItem={item}
                    size={size}
                    settings={settings}
                    rotationScale={rotScale}
                />
            ))}
        </div>
    );
}
